// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * cm-coding: Each user should notice the new Connectors and Vault links once,
 * without creating durable workspace state for a temporary navigation hint.
 * Store acknowledgements per user in this browser so visiting one feature
 * clears only its badge and another user on the same device retains theirs.
 */
import { useEffect, useState } from 'react'

const NEW_SIDEBAR_PATHS = ['/workspace-connectors', '/vault'] as const
const STORAGE_KEY_PREFIX = 'docreview.sidebar.seen:'

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`
}

function readSeenPaths(userId: string): Set<string> {
  try {
    const value = JSON.parse(window.localStorage.getItem(storageKey(userId)) ?? '[]')
    if (!Array.isArray(value) || value.some(path => typeof path !== 'string')) return new Set()
    return new Set(value)
  } catch {
    return new Set()
  }
}

function visitedNewPath(pathname: string): string | undefined {
  return NEW_SIDEBAR_PATHS.find(path => pathname === path || pathname.startsWith(`${path}/`))
}

function seenPathsForLocation(userId: string | null, pathname: string): Set<string> {
  if (!userId) return new Set()

  const seenPaths = readSeenPaths(userId)
  const visitedPath = visitedNewPath(pathname)
  if (visitedPath) seenPaths.add(visitedPath)

  return seenPaths
}

function unseenPaths(seenPaths: ReadonlySet<string>): Set<string> {
  return new Set(NEW_SIDEBAR_PATHS.filter(path => !seenPaths.has(path)))
}

export function useNewSidebarItems(userId: string | null, pathname: string): ReadonlySet<string> {
  const [unseen, setUnseen] = useState(() => unseenPaths(seenPathsForLocation(userId, pathname)))

  useEffect(() => {
    const seenPaths = seenPathsForLocation(userId, pathname)
    if (userId) {
      try {
        window.localStorage.setItem(storageKey(userId), JSON.stringify([...seenPaths]))
      } catch {
        // cm-coding: A blocked or full localStorage must not restore a badge the
        // user just cleared. Keep the acknowledgement in React state for this
        // session even when it cannot be saved for the next visit.
      }
    }
    setUnseen(unseenPaths(seenPaths))
  }, [pathname, userId])

  return unseen
}
